import React, { useContext, useEffect, useId, useState } from 'react';
import { useUserEvent } from '../hooks';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { ThemeContext } from '../App';
import {
  FaArrowLeft,
  FaCheck,
  FaChartLine,
  FaClock,
  FaCrown,
  FaHistory,
  FaRocket,
  FaShieldAlt,
} from 'react-icons/fa';
import PlanBadge from './PlanBadge';
import UpgradeModal from './UpgradeModal';
import { MercadoPagoService } from '../services/MercadoPagoService';
import { PlanService } from '../services/PlanService';
import { PAYMENT_CATALOG, formatCurrency, getPlanName, getUpgradeOptions } from '../config/paymentCatalog';
import './account-platform.css';

export default function MeusPlanos({ user, onBack }) {
  useUserEvent({ userId: user?.id, tela: 'MeusPlanos', evento: 'page_view', comDuração: true });
  const { darkMode } = useContext(ThemeContext);
  const { plano, limites, loading } = usePlanLimits(user?.id);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [assinaturas, setAssinaturas] = useState([]);
  const [cobrancas, setCobrancas] = useState([]);
  const [gerenciandoAssinatura, setGerenciandoAssinatura] = useState(null);
  const [mensagemAssinatura, setMensagemAssinatura] = useState(null);
  const [confirmacaoCancelamento, setConfirmacaoCancelamento] = useState(null);
  const tituloConfirmacaoId = useId();
  const descricaoConfirmacaoId = useId();
  
  // Tipo do plano atual
  const planoTipo = plano?.tipo || 'starter';

  const inputStyle = {
    padding: '12px',
    borderRadius: 8,
    border: darkMode ? '1.5px solid #90caf9' : '1.5px solid #1976d2',
    width: '100%',
    fontSize: 16,
    outline: 'none',
    background: darkMode ? '#232526' : '#f8fafc',
    color: darkMode ? '#f8fafc' : '#333',
    boxSizing: 'border-box',
  };

  // Informações dos planos
  const planosInfo = {
    sensor_free: {
      nome: 'Gratuito Sensor',
      preco: 'Vitalício',
      cor: PAYMENT_CATALOG.sensor_free.color,
      icone: <FaChartLine />
    },
    starter: {
      nome: 'Starter Trial',
      preco: '30 dias',
      cor: '#43cea2',
      icone: <FaRocket />
    },
    premium: {
      nome: getPlanName('premium'),
      preco: `${formatCurrency(PAYMENT_CATALOG.premium.monthly)}/mês`,
      cor: '#667eea',
      icone: <FaChartLine />
    },
    pro: {
      nome: getPlanName('pro'),
      preco: `${formatCurrency(PAYMENT_CATALOG.pro.monthly)}/mês`,
      cor: '#f093fb',
      icone: <FaCrown />
    },
    essential: {
      nome: getPlanName('essential'),
      preco: `${formatCurrency(PAYMENT_CATALOG.essential.monthly)}/mês`,
      cor: PAYMENT_CATALOG.essential.color,
      icone: <FaChartLine />
    },
    professional: {
      nome: getPlanName('professional'),
      preco: `${formatCurrency(PAYMENT_CATALOG.professional.monthly)}/mês`,
      cor: PAYMENT_CATALOG.professional.color,
      icone: <FaRocket />
    },
    management: {
      nome: getPlanName('management'),
      preco: `${formatCurrency(PAYMENT_CATALOG.management.monthly)}/mês`,
      cor: PAYMENT_CATALOG.management.color,
      icone: <FaShieldAlt />
    },
    enterprise: {
      nome: 'Enterprise',
      preco: 'Sob Consulta',
      cor: '#1A237E',
      icone: <FaCrown />
    }
  };

  const planoInfo = planosInfo[planoTipo] || planosInfo.starter;
  const podeUpgrade = getUpgradeOptions(planoTipo).length > 0 && planoTipo !== 'enterprise';
  const assinaturaBaseAtiva = assinaturas.some(item =>
    ['premium', 'pro', 'essential', 'professional', 'management'].includes(item.produto)
      && ['pending', 'authorized', 'paused'].includes(item.status)
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    PlanService.getAssinaturasMercadoPago(user.id).then(result => {
      if (cancelled || !result.success) return;
      setAssinaturas(result.data.assinaturas);
      setCobrancas(result.data.cobrancas);
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  const gerenciarAssinatura = async (assinatura, acao) => {
    setGerenciandoAssinatura(assinatura.preapproval_id);
    setMensagemAssinatura(null);
    try {
      const updated = await MercadoPagoService.gerenciarAssinatura(assinatura.preapproval_id, acao);
      setAssinaturas(current => current.map(item => item.preapproval_id === assinatura.preapproval_id
        ? { ...item, status: updated.status }
        : item));
      const sucesso = acao === 'cancelar'
        ? 'As próximas cobranças foram canceladas. O acesso já pago permanece até o fim do ciclo.'
        : acao === 'pausar'
          ? 'A assinatura foi pausada e não terá novas cobranças enquanto estiver assim.'
          : 'A assinatura foi reativada.';
      setMensagemAssinatura({ type: 'success', text: sucesso });
    } catch (error) {
      setMensagemAssinatura({ type: 'error', text: error.message || 'Não foi possível atualizar a assinatura.' });
    } finally {
      setGerenciandoAssinatura(null);
    }
  };

  const confirmarCancelamento = () => {
    const assinatura = confirmacaoCancelamento;
    if (!assinatura) return;
    setConfirmacaoCancelamento(null);
    gerenciarAssinatura(assinatura, 'cancelar');
  };

  const retomarCheckout = async assinatura => {
    setGerenciandoAssinatura(assinatura.preapproval_id);
    setMensagemAssinatura(null);
    try {
      await MercadoPagoService.abrirCheckout({
        plano: assinatura.produto,
        periodicidade: assinatura.periodicidade,
        usuario: user,
      });
    } catch (error) {
      setMensagemAssinatura({ type: 'error', text: error.message || 'Não foi possível retomar o pagamento.' });
      setGerenciandoAssinatura(null);
    }
  };

  return (
    <>
      <div className={`account-plans-shell${darkMode ? ' is-dark' : ''}`} style={{
        padding: '1.5rem',
        maxWidth: 800,
        margin: '0 auto',
        background: darkMode ? '#232526' : '#f8fafc',
        minHeight: '100vh',
        fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
      }}>
        <header className="plans-page-hero">
          <div>
            <span className="account-eyebrow"><FaCrown /> Assinatura ClimoraX</span>
            <h1>Plano e recursos</h1>
            <p>Veja o que está contratado, os limites da conta e os módulos adicionais disponíveis.</p>
          </div>
          <span className="plans-security-chip"><FaShieldAlt /> Gestão segura</span>
        </header>

        {loading && (
          <div style={{
            background: darkMode ? '#2a2d2e' : '#fff',
            borderRadius: 12,
            padding: '1.5rem',
            marginBottom: 24,
            textAlign: 'center',
            color: darkMode ? '#aaa' : '#666'
          }}>
            Carregando plano...
          </div>
        )}
        {!loading && (
        <>
        {/* Card do Plano Atual */}
        <section className="plans-current-card" style={{
          background: darkMode ? '#2a2d2e' : '#fff',
          borderRadius: 12,
          padding: '1.5rem',
          marginBottom: 24,
          boxShadow: `0 8px 24px ${planoInfo.cor}44`,
          border: `3px solid ${planoInfo.cor}`,
          position: 'relative',
          overflow: 'visible'
        }}>
          {/* Badge PLANO ATUAL */}
          <div style={{
            position: 'absolute',
            top: -12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: `linear-gradient(135deg, ${planoInfo.cor}, ${planoInfo.cor}dd)`,
            color: '#fff',
            padding: '6px 20px',
            borderRadius: 20,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1,
            boxShadow: `0 4px 12px ${planoInfo.cor}66`,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap'
          }}>
            ✓ Plano Atual
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: 16,
            marginTop: 8
          }}>
            <div>
              <div style={{ 
                fontSize: 28, 
                fontWeight: 700,
                color: planoInfo.cor,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                {planoInfo.icone}
                {planoInfo.nome}
              </div>
            </div>
            <PlanBadge plano={planoTipo} />
          </div>

          <div style={{
            fontSize: 20,
            fontWeight: 600,
            color: darkMode ? '#90caf9' : '#1976d2',
            marginBottom: 16
          }}>
            {planoInfo.preco}
          </div>

          {/* Limites do Plano */}
          {!loading && limites && (
            <div className="plans-limits-panel" style={{
              background: darkMode ? '#1a1d1e' : '#f8fafc',
              borderRadius: 8,
              padding: '1rem',
              marginTop: 16
            }}>
              <div style={{
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 12,
                color: darkMode ? '#ffd54f' : '#1976d2'
              }}>
                Limites do Plano
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <LimiteItem
                  label="Propriedades"
                  limite={limites.propriedades}
                  darkMode={darkMode}
                />
                <LimiteItem
                  label="Talhões"
                  limite={limites.talhoes}
                  darkMode={darkMode}
                />
                <LimiteItem
                  label="Sensores"
                  limite={limites.sensores}
                  darkMode={darkMode}
                />
                <LimiteItem
                  label="Alertas Simultâneos"
                  limite={limites.alertas}
                  darkMode={darkMode}
                />
              </div>
            </div>
          )}
        </section>

        {assinaturas.length > 0 && (
          <section className="plans-benefits-card" style={{
            background: darkMode ? '#2a2d2e' : '#fff',
            borderRadius: 12,
            padding: '1.5rem',
            marginBottom: 24,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 6px', color: darkMode ? '#ffd54f' : '#1976d2', fontSize: 18 }}>
              <FaClock /> Assinaturas recorrentes
            </h3>
            <p style={{ margin: '0 0 16px', color: darkMode ? '#aaa' : '#64748b', fontSize: 13 }}>
              O Mercado Pago cobra automaticamente no cartão conforme a periodicidade contratada.
            </p>
            {mensagemAssinatura && (
              <p
                role={mensagemAssinatura.type === 'error' ? 'alert' : 'status'}
                style={{
                  margin: '0 0 14px', padding: '10px 12px', borderRadius: 8,
                  background: mensagemAssinatura.type === 'error' ? '#fff0ef' : '#eaf8ef',
                  color: mensagemAssinatura.type === 'error' ? '#9c2f29' : '#176437',
                  fontSize: 13, fontWeight: 600,
                }}
              >
                {mensagemAssinatura.text}
              </p>
            )}
            <div style={{ display: 'grid', gap: 10 }}>
              {assinaturas.map(assinatura => {
                const produto = PAYMENT_CATALOG[assinatura.produto];
                const podeCancelar = ['pending', 'authorized', 'paused'].includes(assinatura.status);
                return (
                  <div key={assinatura.preapproval_id} style={{
                    border: `1px solid ${darkMode ? '#404448' : '#dce6ed'}`,
                    borderRadius: 10,
                    padding: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap'
                  }}>
                    <div>
                      <strong style={{ display: 'block', fontSize: 14 }}>{produto?.name || assinatura.produto}</strong>
                      <span style={{ display: 'block', marginTop: 3, color: darkMode ? '#aaa' : '#64748b', fontSize: 12 }}>
                        {formatCurrency(assinatura.valor)} · {assinatura.periodicidade === 'anual' ? 'cobrança anual' : 'cobrança mensal'}
                      </span>
                      <span style={{ display: 'block', marginTop: 3, color: darkMode ? '#aaa' : '#64748b', fontSize: 12 }}>
                        {assinatura.status === 'canceled'
                          ? 'Cancelada, sem novas cobranças'
                          : assinatura.proxima_cobranca
                            ? `Próxima cobrança: ${new Date(assinatura.proxima_cobranca).toLocaleDateString('pt-BR')}`
                            : `Status: ${assinatura.status}`}
                      </span>
                    </div>
                    {podeCancelar && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {assinatura.status === 'pending' && (
                          <button
                            type="button"
                            disabled={gerenciandoAssinatura === assinatura.preapproval_id}
                            onClick={() => retomarCheckout(assinatura)}
                            style={{ border: '1px solid #176dcc', borderRadius: 8, padding: '8px 10px', color: '#1459a6', background: 'transparent', fontWeight: 700, cursor: 'pointer' }}
                          >
                            {gerenciandoAssinatura === assinatura.preapproval_id ? 'Abrindo...' : 'Continuar pagamento'}
                          </button>
                        )}
                        {assinatura.status === 'authorized' && (
                          <button
                            type="button"
                            disabled={gerenciandoAssinatura === assinatura.preapproval_id}
                            onClick={() => gerenciarAssinatura(assinatura, 'pausar')}
                            style={{ border: '1px solid #8a6d1d', borderRadius: 8, padding: '8px 10px', color: '#72570d', background: 'transparent', fontWeight: 700, cursor: 'pointer' }}
                          >
                            Pausar cobranças
                          </button>
                        )}
                        {assinatura.status === 'paused' && (
                          <button
                            type="button"
                            disabled={gerenciandoAssinatura === assinatura.preapproval_id}
                            onClick={() => gerenciarAssinatura(assinatura, 'reativar')}
                            style={{ border: '1px solid #25824b', borderRadius: 8, padding: '8px 10px', color: '#176437', background: 'transparent', fontWeight: 700, cursor: 'pointer' }}
                          >
                            Reativar cobranças
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={gerenciandoAssinatura === assinatura.preapproval_id}
                          onClick={() => setConfirmacaoCancelamento(assinatura)}
                          style={{ border: '1px solid #d45b55', borderRadius: 8, padding: '8px 10px', color: '#b43e38', background: 'transparent', fontWeight: 700, cursor: 'pointer' }}
                        >
                          {gerenciandoAssinatura === assinatura.preapproval_id ? 'Atualizando...' : 'Cancelar cobranças'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {cobrancas.length > 0 && (
          <section className="plans-benefits-card" style={{
            background: darkMode ? '#2a2d2e' : '#fff',
            borderRadius: 12,
            padding: '1.5rem',
            marginBottom: 24,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ margin: '0 0 14px', color: darkMode ? '#ffd54f' : '#1976d2', fontSize: 18 }}>
              <FaHistory /> Histórico de cobranças
            </h3>
            <div style={{ display: 'grid', gap: 8 }}>
              {cobrancas.slice(0, 8).map(cobranca => (
                <div key={cobranca.authorized_payment_id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, padding: '9px 0', borderBottom: `1px solid ${darkMode ? '#3d4144' : '#edf2f5'}`, fontSize: 12 }}>
                  <span>
                    <strong>{formatCurrency(cobranca.valor)}</strong>
                    <small style={{ display: 'block', marginTop: 2, color: darkMode ? '#aaa' : '#7b8d99' }}>
                      Transação {cobranca.payment_id || cobranca.authorized_payment_id}
                    </small>
                  </span>
                  <span style={{ textAlign: 'right' }}>
                    <strong>{cobranca.status === 'approved' ? 'Aprovada' : cobranca.status}</strong>
                    <small style={{ display: 'block', marginTop: 2, color: darkMode ? '#aaa' : '#7b8d99' }}>
                      {new Date(cobranca.cobrado_em || cobranca.criado_em).toLocaleDateString('pt-BR')}
                    </small>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Benefícios do Plano */}
        <section className="plans-benefits-card" style={{
          background: darkMode ? '#2a2d2e' : '#fff',
          borderRadius: 12,
          padding: '1.5rem',
          marginBottom: 24,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{
            fontSize: 18,
            fontWeight: 600,
            marginBottom: 16,
            color: darkMode ? '#ffd54f' : '#1976d2'
          }}>
            ✨ Benefícios Inclusos
          </h3>
          <div className="plans-benefits-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(PAYMENT_CATALOG[planoTipo]?.features?.length
              ? PAYMENT_CATALOG[planoTipo].features
              : ['Previsões meteorológicas detalhadas', 'Gestão de propriedades e talhões', 'Caderno de campo e alertas']
            ).map(item => <BeneficioItem key={item} texto={item} darkMode={darkMode} />)}
          </div>
        </section>
        </>
        )}

        {/* Botões de Ação */}
        {!loading && (
        <div className="plans-actions-stack" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {podeUpgrade && (
            <button
              onClick={() => {
                if (assinaturaBaseAtiva) {
                  alert('Cancele primeiro as cobranças futuras do plano atual. Seu acesso continuará até o fim do ciclo já pago.');
                  return;
                }
                setShowUpgradeModal(true);
              }}
              style={{
                ...inputStyle,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                fontSize: 16,
                padding: '14px'
              }}
            >
              <FaCrown />
              {assinaturaBaseAtiva
                ? 'Gerencie a assinatura atual antes de trocar'
                : 'Comparar planos disponíveis'}
            </button>
          )}

          <button
            className="account-back-button"
            onClick={onBack}
            style={{
              ...inputStyle,
              background: darkMode ? '#444' : '#eee',
              color: darkMode ? '#ffd54f' : '#1976d2',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            <FaArrowLeft /> Voltar ao painel
          </button>
        </div>
        )}
      </div>

      {/* Modal de Upgrade */}
      <UpgradeModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        planoAtual={planoTipo}
        recurso="upgrade"
        usuario={user}
      />

      {confirmacaoCancelamento && (
        <div
          className={`plans-confirm-backdrop${darkMode ? ' is-dark' : ''}`}
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setConfirmacaoCancelamento(null);
          }}
        >
          <section
            className="plans-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={tituloConfirmacaoId}
            aria-describedby={descricaoConfirmacaoId}
            onKeyDown={event => {
              if (event.key === 'Escape') setConfirmacaoCancelamento(null);
            }}
          >
            <span className="plans-confirm-icon" aria-hidden="true"><FaShieldAlt /></span>
            <h2 id={tituloConfirmacaoId}>Cancelar próximas cobranças?</h2>
            <p id={descricaoConfirmacaoId}>
              A renovação automática do plano{' '}
              <strong>{PAYMENT_CATALOG[confirmacaoCancelamento.produto]?.name || confirmacaoCancelamento.produto}</strong>{' '}
              será encerrada. O acesso já pago continuará disponível até o fim do ciclo atual.
            </p>
            <div className="plans-confirm-actions">
              <button
                type="button"
                autoFocus
                className="plans-confirm-keep"
                onClick={() => setConfirmacaoCancelamento(null)}
              >
                Manter assinatura
              </button>
              <button
                type="button"
                className="plans-confirm-cancel"
                onClick={confirmarCancelamento}
              >
                Cancelar próximas cobranças
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

// Componente auxiliar para exibir limites
function LimiteItem({ label, limite, darkMode }) {
  const isIlimitado = limite === null || limite === -1 || limite === 999999;
  
  return (
    <div className="plans-limit-row" style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: 14
    }}>
      <span style={{ color: darkMode ? '#ccc' : '#666' }}>{label}:</span>
      <span style={{
        fontWeight: 600,
        color: isIlimitado ? '#43cea2' : (darkMode ? '#90caf9' : '#1976d2')
      }}>
        {isIlimitado ? '∞ Ilimitado' : limite}
      </span>
    </div>
  );
}

// Componente auxiliar para benefícios
function BeneficioItem({ texto, darkMode }) {
  return (
    <div className="plans-benefit-row" style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      fontSize: 14,
      color: darkMode ? '#ccc' : '#666'
    }}>
      <FaCheck style={{ 
        color: '#43cea2',
        fontSize: 16,
        flexShrink: 0
      }} />
      <span>{texto}</span>
    </div>
  );
}
